import { addDays, differenceInCalendarDays, format, parseISO } from "date-fns";
import autoTable from "jspdf-autotable";
import type jsPDF from "jspdf";
import { getCyclePhaseSpans, isPostPeakPhaseDay } from "../cyclePhases";
import type { NFPMethod } from "../cycleLogic";
import { getCycleTiming } from "../cycleTiming";

interface Observation {
  date: string;
  cycle_day: number;
  bleeding?: number;
  monitor?: string;
  intercourse?: boolean;
  mucus?: string;
  mucus_consistency?: string | null;
  mucus_color?: string | null;
  notes?: string;
  symptoms?: string[];
  temperature_f?: number;
  ovulation_test?: string | null;
}

interface Cycle {
  start_date: string;
  end_date?: string | null;
  first_peak_cd?: number | null;
}

const MAX_CYCLE_COLUMNS = 35;

const getCellToken = (obs?: Observation, isPostPeakNonFertile = false) => {
  if (!obs) return "";

  const parts: string[] = [];
  if (obs.bleeding) parts.push(`B${obs.bleeding}`);
  if (obs.monitor) parts.push(obs.monitor.slice(0, 1).toUpperCase());
  if (obs.mucus) parts.push(isPostPeakNonFertile ? "Z" : "M");
  if (obs.intercourse) parts.push("I");
  if (obs.temperature_f) parts.push("T");
  if (obs.symptoms && obs.symptoms.length > 0) parts.push("S");
  if (obs.ovulation_test === "positive") parts.push("O");
  return parts.join("/");
};

/**
 * Day-1-aligned grid of the five most recent cycles. `cycles` must be the recorded cycles
 * only (no partial cycle) — they're numbered the same way as the Cycle Summary table
 * (oldest recorded cycle = 1). `todayKey` is the owner's today.
 */
export const addCycleAlignmentTable = (
  doc: jsPDF,
  observations: Observation[],
  cycles: Cycle[],
  options: { methods?: readonly string[]; todayKey?: string } = {}
) => {
  const allSorted = [...cycles].sort((a, b) => b.start_date.localeCompare(a.start_date));
  const sortedCycles = allSorted.slice(0, 5);

  if (!sortedCycles.length) {
    return;
  }

  const observationsByDate = new Map(observations.map((obs) => [obs.date, obs]));
  const todayKey = options.todayKey ?? format(new Date(), "yyyy-MM-dd");

  const cycleRows = sortedCycles.map((cycle, index) => {
    const start = parseISO(cycle.start_date);
    const endKey = cycle.end_date || todayKey;
    const safeEnd = endKey < cycle.start_date ? cycle.start_date : endKey;
    const end = parseISO(safeEnd);
    const cycleLength = Math.max(1, differenceInCalendarDays(end, start) + 1);
    const timing = getCycleTiming(cycle, todayKey);
    const cycleNumber = allSorted.length - index;
    const when = timing === "past" ? "" : `, ${timing}`;
    const label = `Cycle ${cycleNumber} (${format(start, "MMM d")}${when})`;

    // Post-peak greying ("Z") follows the method's own close from the phase spans — the
    // Sympto-Thermal double check, a restarted mucus count, an unmarked next period — never a
    // flat Peak + 3.
    const cycleObservations = Array.from({ length: cycleLength }, (_, dayIdx) => {
      const obs = observationsByDate.get(format(addDays(start, dayIdx), "yyyy-MM-dd"));
      return obs ? { ...obs, cycle_day: dayIdx + 1 } : null;
    }).filter((obs): obs is Observation => obs !== null);
    const spans = getCyclePhaseSpans(cycle, cycleObservations, {
      methods: (options.methods ?? ["Marquette"]) as NFPMethod[],
      today: todayKey,
    });

    return {
      label,
      cycleLength,
      start,
      spans,
    };
  });

  const maxCycleDay = Math.min(
    MAX_CYCLE_COLUMNS,
    cycleRows.reduce((max, row) => Math.max(max, row.cycleLength), 1)
  );

  const head = [["Cycle", ...Array.from({ length: maxCycleDay }, (_, idx) => `${idx + 1}`)]];
  const body = cycleRows.map((row) => {
    const cells = Array.from({ length: maxCycleDay }, (_, dayIdx) => {
      if (dayIdx + 1 > row.cycleLength) {
        return "";
      }

      const dateKey = format(addDays(row.start, dayIdx), "yyyy-MM-dd");
      const obs = observationsByDate.get(dateKey);
      const isPostPeakNonFertile = isPostPeakPhaseDay(dayIdx + 1, row.spans);
      return getCellToken(obs, isPostPeakNonFertile);
    });

    return [row.label, ...cells];
  });

  autoTable(doc, {
    startY: 28,
    head,
    body,
    theme: "grid",
    styles: { fontSize: 6, cellPadding: 1.2, halign: "center" },
    headStyles: { fillColor: [99, 102, 241], halign: "center" },
    columnStyles: {
      0: { cellWidth: 34, halign: "left" },
    },
    didParseCell: (hookData) => {
      if (hookData.section !== "body" || hookData.column.index === 0) return;

      const token = String(hookData.cell.raw || "");
      if (!token) return;

      if (token.includes("B")) {
        hookData.cell.styles.fillColor = [254, 226, 226];
      } else if (token.includes("O")) {
        // Positive OPK (LH surge) — checked before the mucus/monitor/temp fills so the one
        // day it marks isn't swallowed by the more common tokens sharing the cell.
        hookData.cell.styles.fillColor = [250, 232, 255];
      } else if (token.includes("Z")) {
        // Mucus seen more than 3 days past Peak Day — not interpreted as fertile.
        hookData.cell.styles.fillColor = [226, 232, 240];
      } else if (token.includes("P") || token.includes("H") || token.includes("M")) {
        hookData.cell.styles.fillColor = [219, 234, 254];
      } else if (token.includes("I")) {
        hookData.cell.styles.fillColor = [254, 243, 199];
      } else if (token.includes("T")) {
        hookData.cell.styles.fillColor = [220, 252, 231];
      }
    },
  });

  const exceedsMaxColumns = cycleRows.some((row) => row.cycleLength > MAX_CYCLE_COLUMNS);
  if (exceedsMaxColumns) {
    const y = (doc as unknown as { lastAutoTable?: { finalY?: number } }).lastAutoTable?.finalY || 180;
    doc.setFontSize(8);
    doc.text(
      `Note: Showing the first ${MAX_CYCLE_COLUMNS} cycle days for readability.`,
      14,
      Math.min(y + 8, 285)
    );
  }
};
